import { Spinner } from "@/components/ui/spinner";
import { isNonProduction, resolveFlexSiteUrl } from "@/lib/utils";
import {
  trackAddPaymentInfo,
  trackViewPaymentStep,
  trackViewShippingStep,
} from "@/lib/analytics/ga4";
import { CheckoutLayout } from "@/components/checkout/checkout-layout";
import { useTranslation } from "react-i18next";
import { isErrorCode } from "@/lib/error-messages";
import { useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { Elements } from "@stripe/react-stripe-js";
import { StripeElementsOptions } from "@stripe/stripe-js";
import SecurityHeader from "@/containers/security-header";
import OrderSummary from "@/containers/order-summary";
import CheckoutError from "@/components/checkout/checkout-error";
import posthog from "posthog-js";
import PaymentForm from "@/containers/payment-form";
import {
  selectOperators,
  selectOperatorsError,
  selectOperatorsLoading,
} from "@/store/selectors/operator.selectors";
import { OperatorsSection } from "@/containers/operators-section";
import {
  BankIdData,
  CheckoutStep,
  CheckoutStepConfig,
  DeliveryData,
  PaymentData,
  StepComponentType,
  StepDataMap,
} from "@/lib/types/checkout-steps";
import {
  readCheckout3dsSnapshot,
} from "@/lib/stripe-3ds-snapshot";
import { BankIdStep } from "@/components/steps/BankIdStep";
import { DeliveryStep } from "@/components/steps/DeliveryStep";
import { PaymentStep } from "@/components/steps/PaymentStep";
import { StepCard } from "@/components/step-cards/StepCard";
import { stripePromise } from "@/lib/utils/stripe";
import { BankIdSummary } from "@/components/steps/bankid-step-summary";
import { DeliverySummary } from "@/components/steps/delivery-step-summary";
import {
  selectCreateOrderState,
  selectCurrentStep,
  selectFetchedOrder,
  selectOrderPromotion,
  selectStepStates,
} from "@/store/selectors/order.selector";
import { fetchOperators } from "@/store/thunks/operator.thunks";
import { fetchOrder, updateOrder } from "@/store/thunks/order.thunks";
import {
  completeBankIdStep,
  completeDeliveryStep,
  completePaymentStep,
  editStep,
  setCurrentStep,
} from "@/store/slices/order.slice";
import { restoreBankIdFor3dsResume } from "@/store/slices/bankid.slice";
import { useLanguage } from "@/hooks/useLanguage";
import checkmark from "@/assets/check-mark-accent.svg";

const STRIPE_APPEARANCE = {
  theme: "stripe" as const,
  variables: {
    colorPrimary: "#5F955F",
    colorBackground: "#ffffff",
    colorText: "#202020",
    colorDanger: "#df1b41",
    fontFamily: "Rubik, ui-sans-serif, system-ui, sans-serif",
    spacingUnit: "4px",
    borderRadius: "6px",
  },
} as const;

export function Checkout() {
  const { t } = useTranslation();
  const language = useLanguage();
  const dispatch = useAppDispatch();

  const stripeOptions: StripeElementsOptions = useMemo(
    () => ({
      mode: "setup",
      currency: "sek",
      payment_method_types: ["card"] as string[],
      locale: language === "en" ? "en" : "sv",
      appearance: STRIPE_APPEARANCE,
    }),
    [language]
  );
  const fetchedOrder = useAppSelector(selectFetchedOrder);
  const createOrderState = useAppSelector(selectCreateOrderState);
  const operators = useAppSelector(selectOperators);
  const operatorsLoading = useAppSelector(selectOperatorsLoading);
  const operatorsError = useAppSelector(selectOperatorsError);
  const currentStep = useAppSelector(selectCurrentStep);
  const stepStates = useAppSelector(selectStepStates);
  const promotion = useAppSelector(selectOrderPromotion);
  const hydrated3dsRef = useRef(false);

  const skipBankIdForTest = (() => {
    const params = new URLSearchParams(window.location.search);
    const wantsSkip =
      params.get("skip_bankid") === "1" ||
      params.get("skip-bankid") === "1" ||
      params.get("skip_step1") === "1" ||
      params.get("skip-step1") === "1" ||
      params.get("test_skip_bankid") === "1" ||
      params.get("test-skip-bankid") === "1";

    // Safety: only allow skipping BankID in non-production (localhost + staging).
    return wantsSkip && isNonProduction();
  })();

  useEffect(() => {
    if (!fetchedOrder?.id || hydrated3dsRef.current) return;

    const params = new URLSearchParams(window.location.search);
    if (!params.get("setup_intent_client_secret")) return;

    const snapshot = readCheckout3dsSnapshot(fetchedOrder.id);
    if (!snapshot) {
      // If the 3DS snapshot is missing (e.g. storage cleared / private mode),
      // we must NOT strip the Stripe return params here. The payment form uses
      // `setup_intent_client_secret` to retrieve the SetupIntent and finalize.
      // Stripping early would prevent `/api/create-order` from ever being called.
      return;
    }

    hydrated3dsRef.current = true;

    dispatch(
      restoreBankIdFor3dsResume({
        verificationDetails: snapshot.verificationDetails,
        email: snapshot.bankIdContact.email,
        phone: snapshot.bankIdContact.phone,
      })
    );
    dispatch(completeBankIdStep(snapshot.bankIdStepData));
    dispatch(completeDeliveryStep(snapshot.deliveryStepData));
    dispatch(setCurrentStep(CheckoutStep.PAYMENT));
  }, [dispatch, fetchedOrder?.id]);

  useEffect(() => {
    if (!skipBankIdForTest) return;
    if (!fetchedOrder?.id) return;

    try {
      sessionStorage.setItem(`skip_bankid_order_${fetchedOrder.id}`, "1");
    } catch {
      // Ignore storage errors in private mode; URL fallback still works.
    }

    const firstName = fetchedOrder.billing_address?.first_name?.trim() || "Adam";
    const lastName = fetchedOrder.billing_address?.last_name?.trim() || "Hassan";
    const fullName = `${firstName} ${lastName}`.trim();
    const testEmail = fetchedOrder.customer_email || "adam@refurbly.se";

    const metadataPhone = fetchedOrder.metadata?.["Phone Number"];
    const resolvedPhone =
      typeof metadataPhone === "string" && metadataPhone.trim().length > 0
        ? metadataPhone.trim()
        : "0760945446";

    const metadataPersonalNumber = fetchedOrder.metadata?.["Personal Number"];
    const resolvedPersonalNumber =
      typeof metadataPersonalNumber === "string" &&
      metadataPersonalNumber.trim().length > 0
        ? metadataPersonalNumber.trim()
        : "199001011234";

    const resolvedStreet =
      fetchedOrder.billing_address?.line_1?.trim() || "Björkrisvägen 10 S";
    const resolvedPostalCode =
      fetchedOrder.billing_address?.zip_code?.trim() || "70234";
    const resolvedCity = fetchedOrder.billing_address?.city?.trim() || "Örebro";

    // Minimal BankID “verified” payload for local/manual testing of later steps.
    // This is only for speeding up UI testing and should not be used in production flows.
    const nowYear = new Date().getFullYear();
    const verificationDetails = {
      sessionId: "mock_skip_bankid",
      username: "test",
      userAttributes: {
        name: fullName,
        givenName: firstName,
        surname: lastName,
        personalNumber: resolvedPersonalNumber,
        SPARv2: {
          ns4PersonId: {
            ns4IdNummer: resolvedPersonalNumber,
            ns4Typ: "PERSONNUMMER",
          },
          ns5Sekretessmarkering: "NEJ",
          ns5SkyddadFolkbokforing: "NEJ",
          ns4SenasteAndringSPAR: "",
          ns8Namn: {
            ns2DatumFrom: `${nowYear - 1}-01-01`,
            ns2DatumTill: `${nowYear + 10}-12-31`,
            ns7Fornamn: firstName,
            ns7Tilltalsnamn: firstName,
            ns7Efternamn: lastName,
          },
          ns10Persondetaljer: {
            ns2DatumFrom: `${nowYear - 1}-01-01`,
            ns2DatumTill: `${nowYear + 10}-12-31`,
            ns5Sekretessmarkering: "NEJ",
            ns5SkyddadFolkbokforing: "NEJ",
            ns10Fodelsedatum: "1990-01-01",
            ns10Kon: "MAN",
          },
          ns15Folkbokforingsadress: {
            ns3SvenskAdress: {
              ns2DatumFrom: `${nowYear - 1}-01-01`,
              ns2DatumTill: `${nowYear + 10}-12-31`,
              ns3Utdelningsadress2: resolvedStreet,
              ns3PostNr: resolvedPostalCode,
              ns3Postort: resolvedCity,
            },
          },
        },
      },
    } as const;

    dispatch(
      restoreBankIdFor3dsResume({
        verificationDetails: verificationDetails as any,
        email: testEmail,
        phone: resolvedPhone,
      })
    );

    dispatch(
      completeBankIdStep({
        name: fullName,
        personalNumber: resolvedPersonalNumber,
        email: testEmail,
        phone: resolvedPhone,
      })
    );

    // IMPORTANT: BankID step normally patches the Commerce Layer order with
    // customer_email + metadata. If we skip BankID, we still need those fields,
    // otherwise placing the order fails (customer relationship can't be blank).
    dispatch(
      updateOrder({
        orderId: fetchedOrder.id,
        attributes: {
          customer_email: testEmail,
          metadata: {
            "Personal Number": resolvedPersonalNumber,
            "Phone Number": resolvedPhone,
          },
        },
      })
    )
      .unwrap()
      .then(() => dispatch(fetchOrder(fetchedOrder.id)))
      .catch(() => {
        // If this fails, the user will hit a clear error later in the flow.
        // Keep the skip behavior non-blocking for local UI testing.
      });

    dispatch(setCurrentStep(CheckoutStep.DELIVERY));
  }, [dispatch, fetchedOrder?.id, fetchedOrder?.customer_email, skipBankIdForTest]);

  useEffect(() => {
    if (!isNonProduction() && fetchedOrder) {
      posthog.identify(fetchedOrder.id, {
        email: fetchedOrder.customer_email,
        order_id: fetchedOrder.id,
        order_number: fetchedOrder.id,
        total: fetchedOrder.total_amount,
        currency: "sek",
      });
    }

    const initOperators = async () => {
      dispatch(fetchOperators());
    };

    initOperators();
  }, [dispatch, fetchedOrder]);

  useEffect(() => {
    if (!fetchedOrder) return;
    if (currentStep === CheckoutStep.DELIVERY) {
      trackViewShippingStep(fetchedOrder);
    } else if (currentStep === CheckoutStep.PAYMENT) {
      trackViewPaymentStep(fetchedOrder);
    }
  }, [currentStep, fetchedOrder]);

  const CHECKOUT_STEPS: CheckoutStepConfig[] = [
    {
      id: CheckoutStep.BANK_ID,
      title: t("steps.contact"),
      component: BankIdStep as StepComponentType<CheckoutStep.BANK_ID>,
      completedComponent: BankIdSummary,
    },
    {
      id: CheckoutStep.DELIVERY,
      title: t("steps.delivery"),
      component: DeliveryStep as StepComponentType<CheckoutStep.DELIVERY>,
      completedComponent: DeliverySummary,
    },
    {
      id: CheckoutStep.PAYMENT,
      title: t("steps.payment"),
      component: ({ isActive, onComplete }) => (
        <PaymentStep
          order={fetchedOrder!}
          isActive={isActive}
          onComplete={onComplete}
        />
      ),
    },
  ];

  const visibleCheckoutSteps = skipBankIdForTest
    ? CHECKOUT_STEPS.filter((s) => s.id !== CheckoutStep.BANK_ID)
    : CHECKOUT_STEPS;

  // New step handling functions
  const handleCompleteStep = (
    step: CheckoutStep,
    data: StepDataMap[typeof step]
  ) => {
    switch (step) {
      case CheckoutStep.BANK_ID:
        dispatch(completeBankIdStep(data as BankIdData));
        break;
      case CheckoutStep.DELIVERY:
        dispatch(completeDeliveryStep(data as DeliveryData));
        break;
      case CheckoutStep.PAYMENT:
        dispatch(completePaymentStep(data as PaymentData));
        break;
    }

    const nextStepIndex =
      visibleCheckoutSteps.findIndex((s) => s.id === step) + 1;
    if (nextStepIndex < visibleCheckoutSteps.length) {
      dispatch(setCurrentStep(visibleCheckoutSteps[nextStepIndex].id));
    }
  };

  const handleEditStep = (step: CheckoutStep) => {
    dispatch(editStep(step));
  };

  if (createOrderState.loading) {
    return <Spinner fullScreen message={t("createOrder.loading")} />;
  }

  if (createOrderState.error) {
    const errorMessage = isErrorCode(createOrderState.error)
      ? t(`errors.${createOrderState.error}`)
      : createOrderState.error;
    return <CheckoutError error={errorMessage} />;
  }

  if (!fetchedOrder) {
    return (
      <CheckoutError
        title={t("errors.title")}
        description={t("errors.orderNotFound")}
      />
    );
  }

  if (fetchedOrder.line_items?.length === 0 || fetchedOrder.id === "") {
    return (
      <CheckoutError
        title={t("errors.title")}
        description={t("errors.missingInfo")}
      />
    );
  }

  const handleSuccess = (orderId: string) => {
    trackAddPaymentInfo(fetchedOrder);
    window.location.href = `${resolveFlexSiteUrl("/order-mottagen", language)}?orderId=${orderId}`;
  };

  return (
    <CheckoutLayout>
      <div>
        <h1 className="font-bold text-3xl">{t("pageTitle")}</h1>
        {promotion.hasPromotion && (
          <div className="bg-accent text-white text-base font-medium py-[6px] flex items-center justify-center rounded-6 mt-3">
            {t("discountApplied")}
            <img
              className="inline-block w-4 h-4 ml-1"
              src={checkmark}
              alt="Checkmark"
            />
          </div>
        )}
      </div>

      <OrderSummary order={fetchedOrder} />
      <OperatorsSection
        operators={operators}
        loading={operatorsLoading}
        error={operatorsError}
      />

      {/* Lift Elements up and keep it mounted */}
      <Elements stripe={stripePromise} options={stripeOptions}>
        <div className="space-y-4">
          {visibleCheckoutSteps.map((stepConfig, index) => {
            const StepComponent = stepConfig.component;
            const stepState = stepStates[stepConfig.id];
            const isActive = currentStep === stepConfig.id;

            return (
              <StepCard
                key={stepConfig.id}
                step={index + 1}
                title={stepConfig.title}
                isActive={isActive}
                isCompleted={stepState.completed}
                onEdit={() => handleEditStep(stepConfig.id)}
                stepData={stepState.data}
                StepSummary={stepConfig.completedComponent}
              >
                {stepConfig.id === CheckoutStep.PAYMENT ? (
                  <PaymentForm
                    order={fetchedOrder}
                    onSuccess={handleSuccess}
                    isActive={isActive}
                  />
                ) : (
                  <StepComponent
                    isActive={isActive}
                    isCompleted={stepState.completed}
                    onComplete={(data: any) =>
                      handleCompleteStep(stepConfig.id, data)
                    }
                    onEdit={() => handleEditStep(stepConfig.id)}
                  />
                )}
              </StepCard>
            );
          })}
        </div>
      </Elements>

      {/*  Test payment alone */}
      {/* <Card>
        <CardHeader>
          <CardTitle>{t("cardCheckout.finiliseOrder")}</CardTitle>
          <CardDescription>{t("cardCheckout.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Elements stripe={stripePromise} options={STRIPE_OPTIONS}>
            <PaymentForm order={order} onSuccess={handleSuccess} />
          </Elements>
        </CardContent>
      </Card> */}
      <SecurityHeader />
      
      {/* Support Contact Footer */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600">
          {t("errors.supportContact")}
        </p>
      </div>
    </CheckoutLayout>
  );
}
